import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Building2,
  Calculator,
  ClipboardList,
  FileBox,
  GraduationCap,
  LogOut,
  Menu,
  ScrollText,
  Settings,
  UserCog,
  Users,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";

import { BrandMark } from "@/components/BrandMark";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { LOCALES, useLocale } from "@/lib/locale";
import { cn } from "@/lib/utils";

const SIDEBAR_WIDTH = "w-64";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
  badgeTone?: "magenta" | "gold";
}

function navItemClass({ isActive }: { isActive: boolean }) {
  return cn(
    "group flex items-center gap-3 rounded-lg px-3 py-2 text-[0.8rem] transition",
    isActive
      ? "bg-white/12 text-paper"
      : "text-ice/65 hover:bg-white/6 hover:text-paper",
  );
}

function Badge({ value, tone = "magenta" }: { value: number; tone?: "magenta" | "gold" }) {
  return (
    <span
      className={cn(
        "num ml-auto rounded-full px-1.5 py-0.5 text-[0.58rem] font-semibold",
        tone === "gold" ? "bg-gold text-night" : "bg-magenta text-paper",
      )}
    >
      {value}
    </span>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { locale, setLocale, t } = useLocale();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openInterventions, setOpenInterventions] = useState(0);
  const [pendingArchitects, setPendingArchitects] = useState(0);

  const isStaff = user?.role === "admin" || user?.role === "installer";
  const isAdmin = user?.role === "admin";
  const isArchitect = user?.role === "architect";
  const isSales = user?.role === "admin" || user?.role === "sales";

  useEffect(() => {
    if (!isStaff) return;
    api
      .openInterventions()
      .then(setOpenInterventions)
      .catch(() => setOpenInterventions(0));
  }, [isStaff]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .pendingArchitectCount()
      .then(setPendingArchitects)
      .catch(() => setPendingArchitects(0));
  }, [isAdmin]);

  // Groups hide themselves when empty, so an installer or architect never sees
  // a heading with nothing under it.
  const groups: Array<{ label: string; items: NavItem[] }> = [
    {
      label: t.groupSales,
      items: isSales ? [{ to: "/orders", label: t.orders, icon: ClipboardList }, { to: "/area", label: t.areaCalc, icon: Calculator }] : [],
    },
    {
      label: t.groupOps,
      items: [
        { to: "/projects", label: t.projects, icon: Building2 },
        ...(isStaff
          ? [
              {
                to: "/interventions",
                label: t.interventions,
                icon: Wrench,
                badge: openInterventions,
                badgeTone: "magenta" as const,
              },
            ]
          : []),
      ],
    },
    {
      label: t.groupNetwork,
      items: [
        ...(isAdmin
          ? [
              {
                to: "/architects",
                label: t.architects,
                icon: Users,
                badge: pendingArchitects,
                badgeTone: "gold" as const,
              },
            ]
          : []),
        ...(isStaff ? [{ to: "/academy", label: t.academy, icon: GraduationCap }] : []),
      ],
    },
    {
      label: t.groupSystem,
      items: [
        ...(isAdmin || isArchitect
          ? [{ to: "/toolkit", label: t.toolkit, icon: FileBox }]
          : []),
        ...(isAdmin
          ? [
              { to: "/users", label: t.users, icon: UserCog },
              { to: "/settings", label: t.settings, icon: Settings },
              { to: "/admin", label: t.certificate, icon: ScrollText },
            ]
          : []),
      ],
    },
  ].filter((g) => g.items.length > 0);

  const close = () => setOpen(false);

  const sidebar = (
    <div className="flex h-full flex-col">
      <Link
        to="/projects"
        onClick={close}
        className="flex items-center gap-3 px-5 py-5"
      >
        <BrandMark className="h-9 w-9" />
        <div className="leading-tight">
          <div className="brand-wordmark text-[0.62rem] text-paper">VERSUS</div>
          <div className="text-[0.5rem] uppercase tracking-[0.3em] text-ice/60">
            Surface ID
          </div>
        </div>
      </Link>

      <div className="mx-5 h-px bg-white/10" />

      <nav className="flex-1 overflow-y-auto px-3 py-3">
        {groups.map((group) => (
          <div key={group.label} className="mb-3">
            <div className="px-3 pb-1.5 pt-2 text-[0.55rem] font-semibold uppercase tracking-[0.22em] text-goldbright/80">
              {group.label}
            </div>
            <div className="grid gap-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={close}
                    className={navItemClass}
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={1.6} />
                    <span className="truncate">{item.label}</span>
                    {item.badge ? (
                      <Badge value={item.badge} tone={item.badgeTone} />
                    ) : null}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="mt-auto border-t border-white/10 px-3 py-4">
        <div className="flex gap-1 rounded-full border border-white/15 p-0.5">
          {LOCALES.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLocale(l)}
              className={cn(
                "flex-1 rounded-full px-2 py-1 text-[0.58rem] font-semibold uppercase tracking-[0.16em] transition",
                locale === l ? "bg-white/15 text-paper" : "text-ice/55",
              )}
            >
              {l}
            </button>
          ))}
        </div>

        {user && (
          <div className="mt-3 flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[0.72rem] text-paper/90">
                {user.name}
              </div>
              <div className="text-[0.58rem] uppercase tracking-[0.14em] text-ice/45">
                {user.role}
              </div>
            </div>
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              title={t.logout}
              aria-label={t.logout}
              className="rounded-lg border border-white/15 p-2 text-ice/60 transition hover:text-paper"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.7} />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="app-surface min-h-screen">
      {/* Mobile: a slim top strip carries the hamburger; the sidebar slides in. */}
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-white/10 bg-night/80 px-4 py-3 backdrop-blur-xl lg:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t.menu}
          className="rounded-lg border border-white/15 p-2 text-ice/75"
        >
          <Menu className="h-4 w-4" strokeWidth={1.7} />
        </button>
        <BrandMark className="h-7 w-7" />
        <div className="text-[0.58rem] uppercase tracking-[0.26em] text-ice/60">
          Surface ID
        </div>
      </header>

      {open && (
        <button
          type="button"
          aria-label={t.menu}
          onClick={close}
          className="fixed inset-0 z-40 bg-night/70 backdrop-blur-sm lg:hidden"
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 border-r border-white/10 bg-night/85 backdrop-blur-xl transition-transform duration-200 lg:z-20 lg:translate-x-0",
          SIDEBAR_WIDTH,
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <button
          type="button"
          onClick={close}
          aria-label={t.menu}
          className="absolute right-3 top-4 rounded-lg border border-white/15 p-1.5 text-ice/60 lg:hidden"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.7} />
        </button>
        {sidebar}
      </aside>

      <div className="lg:pl-64">
        <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
          {children}
        </main>
      </div>
    </div>
  );
}
